import { useMemo } from 'react';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useGetProviderVolumeQuery } from 'src/api/redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import {
  applyDisplayMode,
  claimsOrEncounters,
} from 'src/components/Profiles/shared/widgets/utils';

/** Shared data-fetching and computation for ExpectedOpportunity widgets. */
const useExpectedOpportunity = () => {
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const { entityDetails } = useProfile();
  const { showEncountersTotals } = useFlags();

  const entityId = entityDetails?.id;
  const codeSetId = widget?.code_set?.id;
  const encounterId = widget?.encounter?.id;
  const year = widget?.year;
  const averageSalePrice = widget?.average_sale_price;
  const volumeType = widget?.volume_type ?? 'claims';
  const displayMode = widget?.display_mode;

  const hasVolumeSource = !!(codeSetId || encounterId);

  const dateRange =
    year != null ? { gte: String(year), lte: String(year) } : undefined;

  const { data, isLoading } = useGetProviderVolumeQuery(
    {
      providerId: entityId ?? '',
      filter_set: codeSetId ?? undefined,
      cohort_id: encounterId ?? undefined,
      dateRange,
    },
    { skip: !entityId || !hasVolumeSource || averageSalePrice == null }
  );

  const expectedOpportunity = useMemo(() => {
    if (!data || averageSalePrice == null) return null;

    const isCohort = !!encounterId;
    const rawVolume =
      volumeType === 'patients'
        ? Number(data?.patients ?? 0)
        : Number(
            claimsOrEncounters(data, isCohort, showEncountersTotals) ?? 0
          );

    if (rawVolume <= 0) return null;

    const volume = applyDisplayMode(rawVolume, displayMode);
    return volume * averageSalePrice;
  }, [
    data,
    averageSalePrice,
    encounterId,
    volumeType,
    showEncountersTotals,
    displayMode,
  ]);

  const periodLabel =
    displayMode === 'monthly_average' ? 'per month' : 'per year';
  const yearLabel = year ?? new Date().getFullYear();

  return { expectedOpportunity, isLoading, periodLabel, yearLabel };
};

export default useExpectedOpportunity;
